import {
  Box,
  Container,
  Link,
  Stack,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { IconCircleCheck } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import MobileInstallCallout from "../../components/pwa/MobileInstallCallout";
import Button from "../../components/ui/Button";
import { ShareButton } from "../../components/ui/ShareButton";
import Text from "../../components/ui/Text";
import { useToast } from "../../contexts/ToastContext";
import apiService from "../../services/api";
import { formatGameDateTime } from "../../utils/formatters";

const GameSuccess = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const { gameDetails, gameCreated, gameUpdated, playersAdded } =
    location.state || {};

  // State for player data
  const [playersData, setPlayersData] = useState([]);
  const [maxPlayers, setMaxPlayers] = useState(0);
  const [loadingPlayers, setLoadingPlayers] = useState(true);

  const gameId = gameDetails?.id;
  const gameLink = `${window.location.origin}/game-details/${gameId}`;
  const sport = gameDetails?.sport || "Football";
  const gameVenue = gameDetails?.venue || "Venue";

  // Fetch player data from backend
  const fetchPlayerData = async () => {
    try {
      const response = await apiService.fetchGamePlayers(gameId);

      if (response.success) {
        setPlayersData(
          response.data?.sort((a, b) => a.join_date - b.join_date),
        );
        setMaxPlayers(response.max_players || 0);
      }
    } catch (error) {
      showToast(error.error, "error");
    } finally {
      setLoadingPlayers(false);
    }
  };

  // Fetch player data when component mounts
  useEffect(() => {
    if (gameId) {
      setLoadingPlayers(true);
      fetchPlayerData();

      // Set up interval to fetch player data 5 times, every second
      let fetchCount = 0;
      const intervalId = setInterval(() => {
        fetchCount++;
        if (fetchCount < 7) {
          fetchPlayerData();
        } else {
          clearInterval(intervalId);
        }
      }, 1000);

      // Cleanup interval on component unmount
      return () => clearInterval(intervalId);
    }
  }, []);

  // Get game type and player count
  const getGameType = () => {
    if (maxPlayers) {
      const half = maxPlayers / 2;
      return half ? `${half}v${half}` : "";
    }
    return ""; // Default fallback
  };

  // Create share message
  const createShareMessage = () => {
    const dateTime = formatGameDateTime(
      gameDetails?.datetime,
      gameDetails?.duration_minutes,
    );
    const gameType = getGameType();

    // Build player list
    const playersListText = playersData
      ?.map((player, index) => {
        return `${index + 1}. ${player?.first_name || ""} ${player?.last_name || ""}
`;
      })
      .join("");

    const playerCount = playersData?.length || 0;

    return `${gameType} ${sport} at ${gameVenue}
${dateTime}

Player List (${playerCount}/${maxPlayers}):
${playersListText}
Join the game: ${gameLink}`;
  };

  const handleClose = () => {
    navigate(`/game-details/${gameId}`);
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        py: 4,
        backgroundColor: isDesktop ? "#f5f5f5" : "white",
      }}
    >
      <Container
        maxWidth={false}
        sx={{
          maxWidth: "420px",
          width: "100%",
          backgroundColor: isDesktop ? "white" : "transparent",
          borderRadius: isDesktop ? "8px" : 0,
          p: isDesktop ? 3 : 2,
          boxShadow: isDesktop ? "0 2px 8px rgba(0,0,0,0.1)" : "none",
          height: "fit-content",
        }}
      >
        {/* Success Header */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
          <IconCircleCheck size={24} color="#52C41A" />
          <Text variant="" sx={{ fontWeight: 700, fontSize: "16px" }}>
            {gameCreated && "Game created"}
            {gameUpdated && "Game updated"}
            {playersAdded && "Players added"}
            {!gameCreated && !gameUpdated && !playersAdded && "You're in!"}
          </Text>
        </Box>
        <Box sx={{ display: "flex", alignItems: "start", gap: 1, mb: 3 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 24,
              height: 24,
            }}
          >
            <Box
              sx={{
                color: "#fff",
                height: 20,
                width: 20,
                borderRadius: "50%",
                backgroundColor: "#000",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 700,
                fontSize: "14px",
              }}
            >
              2
            </Box>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Text variant="" sx={{ fontWeight: 700, fontSize: "16px" }}>
              Share the player list
            </Text>
            <Text variant="paragraph3" sx={{ color: "#000", fontSize: "16px" }}>
              {(gameCreated || gameUpdated) &&
                "Share the player list with your group to collect sign-ups."}
              {playersAdded && "Let the group know they’ve joined."}
              {!gameCreated &&
                !gameUpdated &&
                !playersAdded &&
                "Let the group know you’ve joined"}
            </Text>
          </Box>
        </Box>

        {/* Game Details Card */}
        <Box sx={{ display: "flex", alignItems: "start", gap: 1, mb: 3 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 24,
              height: 24,
            }}
          ></Box>
          <Box
            sx={{
              borderRadius: "8px",
              p: 1,
              backgroundColor: "#F0F0F0",
              width: "100%",
            }}
          >
            <Stack>
              {/* Game Type and Venue */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Text variant="paragraph">
                  {getGameType()} {sport} at {gameVenue}
                </Text>
              </Box>

              {/* Date and Time */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Text variant="paragraph">
                  {formatGameDateTime(
                    gameDetails?.datetime,
                    gameDetails?.duration_minutes,
                  )}
                </Text>
              </Box>

              {/* Player List */}
              <Box sx={{ mb: 3 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mt: 2,
                  }}
                >
                  <Text variant="paragraph">
                    Player List ({playersData?.length || 1}/{maxPlayers}):
                  </Text>
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  {loadingPlayers ? (
                    <Text variant="paragraph3">Loading players...</Text>
                  ) : (
                    playersData?.map((player, index) => {
                      return (
                        <Text key={`player-${index}`} variant="paragraph">
                          {index + 1}. {player.first_name} {player.last_name}
                        </Text>
                      );
                    })
                  )}
                </Box>
              </Box>

              {/* Join Link */}
              <Box>
                <Text variant="paragraph">Join the game: </Text>
                <Link href={`/game-details/${gameId}`}>{gameLink}</Link>
              </Box>
            </Stack>
          </Box>
        </Box>

        <MobileInstallCallout />

        {/* Action Buttons */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
            mt: 2,
          }}
        >
          <ShareButton text={createShareMessage()} />

          <Button
            variant="secondary"
            onClick={handleClose}
            sx={{ width: "auto" }}
          >
            Close
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default GameSuccess;
